import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {
    BodyScrollEndEvent,
    BodyScrollEvent,
    CellKeyDownEvent,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    RowClickedEvent,
    SortChangedEvent
} from 'ag-grid-community';
import {AppGrid, HEADER_HEIGHT} from './AppGrid.component';
import {useAvailableHeight} from './useAvailableHeight';
import {isLastRowRendered} from './gridPaging';
import {buildColumnDefs, buildSortFilters, CLEAR_ALL_COL} from './appDataGrid.function';
import {DataGridFilterContext, NO_FILTER} from './AppDataGridFilter.component';
import {Loader, LoaderSize} from 'components/common/Loader.component';
// the unwrapped footer, handed the translate this grid was given - its default export is
// connected, and nothing here should need a store
import {TableFooter} from 'components/resizableTable/TableFooter.component';
import {HISTORY_FILTERS, LOADING_STATUS} from 'utils/constants';
import type {
    AppDataGridProps,
    DataGridFilterContextValue,
    DataGridFilters,
    DataGridRow
} from 'types/appDataGrid';
import './appDataGrid.scss';

// as tight as a single line of values reads
const ROW_HEIGHT = 40;

// tall enough for a Gaia input to sit in the row under the headings
const FILTER_ROW_HEIGHT = 46;

// the page the backend serves, which every call site asked for by the same number
const PAGE_SIZE = 40;

// long enough that a filter is finished being typed before the store hears about it
const FILTER_DEBOUNCE_MS = 700;

// what the footer bar pinned to the bottom of the viewport leaves uncovered above itself, which
// is all a page has to keep clear - an action bar of its own sits inside that same band
export const FOOTER_HEIGHT = 50;

/**
 * The app's column-map table, as an ag-grid.
 *
 * It takes what ResizableTable takes - a `columnsMap` list, the tasks, the filters and the
 * actions that refetch - so a page switches renderings by swapping the tag. The filter row is
 * ag-grid's floating filter row (see AppDataGridFilter.component) and the widgets in it reach
 * the filters through context, since ag-grid mounts them itself.
 *
 * A page only gets what it wires up: no `sortingColumnChanged` and the headings do not sort, no
 * `taskSelect` and the checkbox column draws nothing - which is how the legacy table behaved.
 */
export const AppDataGrid = ({
                                tasks,
                                defaultColumns,
                                filters,
                                translate,
                                selectors,
                                loadingStatus,
                                loadingError,
                                loaderSize,
                                asyncLoadingState,
                                className,
                                dataCy,
                                hideSearchRow,
                                hideRowsNumber,
                                noDataString,
                                debounceTimeout,
                                bottomGap,
                                watchSelector,
                                height,
                                scrollTop,
                                sortingLogic,
                                selectedTasks,
                                filterChanged,
                                refreshGrid,
                                sortingColumnChanged,
                                loadMoreTasks,
                                saveScrollPosition,
                                taskSelect,
                                onRow
                            }: AppDataGridProps) => {

    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const refreshTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // one page request per scroll to the bottom, until the rows that arrive move it again
    const pageRequested = useRef(false);
    // the filters as the user has left them, while a typed one waits out its debounce
    const pendingFilters = useRef<DataGridFilters | null>(null);
    // the same, for the filter row to draw from - the ref alone would not redraw it
    const [typedFilters, setTypedFilters] = useState<DataGridFilters | null>(null);
    const scrollOffset = useRef(0);
    const scrollRestored = useRef(false);
    const gridApi = useRef<GridApi<DataGridRow> | null>(null);

    const measuredHeight = useAvailableHeight(gridElement, {
        bottomGap: bottomGap ?? FOOTER_HEIGHT,
        watchSelector,
        // a page's action bar is drawn over the grid, so it stops short of it however little
        // room that leaves
        minHeight: 0
    });
    const gridHeight = height ?? measuredHeight;

    const rows = tasks?.rows ?? [];
    const records = tasks?.records ?? 0;
    const page = tasks?.page ?? 1;
    const isLoading = loadingStatus === LOADING_STATUS.LOADING;
    const showFilters = !hideSearchRow;

    // only a reload sets the loading flag - a later page never does - so the rows it is about to
    // replace come off the screen while it runs, as the legacy table's body does
    const showLoader = isLoading && !asyncLoadingState?.cursor;

    // the page it asked for has landed - a request may go again. Reset on the loading flag too,
    // in case that page brought no rows with it
    useEffect(() => {
        pageRequested.current = false;
    }, [rows.length, isLoading]);

    useEffect(() => () => clearTimeout(refreshTimeout.current), []);

    // the grid reloads on these filters, and nothing is left waiting to change them again
    const commit = useCallback((newFilters: DataGridFilters, changed?: (filters: DataGridFilters) => void) => {
        clearTimeout(refreshTimeout.current);
        pendingFilters.current = null;
        setTypedFilters(null);
        changed?.(newFilters);
        refreshGrid?.(newFilters);
    }, [refreshGrid]);

    // a picked filter searches at once; a typed one waits, since the store empties the grid as
    // soon as it hears - which is a reload per keystroke, and the box left showing one letter
    const applyFilters = useCallback((newFilters: DataGridFilters, whenTypingStops?: boolean) => {
        clearTimeout(refreshTimeout.current);

        if (!whenTypingStops) {
            commit(newFilters, filterChanged);
            return;
        }

        pendingFilters.current = newFilters;
        setTypedFilters(newFilters);
        // read again when it fires, since the page can have moved the filters on under the typing
        refreshTimeout.current = setTimeout(() => commit(pendingFilters.current ?? newFilters, filterChanged),
            debounceTimeout ?? FILTER_DEBOUNCE_MS);
    }, [commit, filterChanged, debounceTimeout]);

    // the page's own filters can change while a search waits - an advanced filter picked, say -
    // and only the filter row's own columns are the typing's to carry over to them
    useEffect(() => {
        if (!pendingFilters.current) {
            return;
        }
        const rebased = {...filters, [HISTORY_FILTERS.COLUMNS]: pendingFilters.current.columns};
        pendingFilters.current = rebased;
        setTypedFilters(rebased);
    }, [filters]);

    const onSearch = useCallback((searchValue: string, value: string | number, whenTypingStops?: boolean) => {
        // what is being typed elsewhere in the row has not reached the store yet, and is not lost
        const current = pendingFilters.current ?? filters;
        const columns = {...current.columns};

        if (value !== NO_FILTER && value !== String(NO_FILTER)) {
            columns[searchValue] = value;
        } else {
            delete columns[searchValue];
        }

        applyFilters({...current, [HISTORY_FILTERS.COLUMNS]: columns}, whenTypingStops);
    }, [filters, applyFilters]);

    // a filter still being typed is cleared along with the rest, rather than searched for after
    const onClearAllFilters = useCallback(() => {
        applyFilters({...(pendingFilters.current ?? filters), [HISTORY_FILTERS.COLUMNS]: {}});
    }, [filters, applyFilters]);

    const selectAll = useCallback(() => {
        taskSelect?.(rows.map((row, index) => index));
    }, [taskSelect, rows]);

    const allRowsSelected = rows.length > 0 && selectedTasks?.length === rows.length;

    const filterContext = useMemo<DataGridFilterContextValue>(() => ({
        // a filter waiting out its debounce still counts as set, to its own box and to clear-all
        filters: typedFilters ?? filters,
        selectors: selectors ?? {},
        translate,
        onSearch,
        onClearAllFilters,
        allRowsSelected: !!allRowsSelected,
        selectAll
    }), [typedFilters, filters, selectors, translate, onSearch, onClearAllFilters, allRowsSelected, selectAll]);

    const columnDefs = useMemo(() => buildColumnDefs(defaultColumns, {
        translate,
        sortable: !!sortingColumnChanged && !!sortingLogic,
        sortingLogic,
        showFilters,
        taskSelect
    }), [defaultColumns, translate, sortingColumnChanged, sortingLogic, showFilters, taskSelect]);

    const loadMore = useCallback(() => {
        loadMoreTasks?.({page: page + 1, rows: PAGE_SIZE, filters});
    }, [loadMoreTasks, page, filters]);

    const onBodyScrollEnd = useCallback(({api}: BodyScrollEndEvent<DataGridRow>) => {
        const hasMore = (rows.length > 0 && rows.length < records) || asyncLoadingState?.cursor != null;

        // a page asked for now is the old filter's, and lands appended to the rows the reload brings
        if (!loadMoreTasks || pageRequested.current || isLoading || pendingFilters.current
            || !hasMore || !isLastRowRendered(api)) {
            return;
        }

        pageRequested.current = true;
        loadMore();
    }, [loadMoreTasks, isLoading, rows.length, records, asyncLoadingState?.cursor, loadMore]);

    const onSortChanged = useCallback(({api}: SortChangedEvent<DataGridRow>) => {
        const sorted = api.getColumnState().find(column => column.sort && column.colId !== CLEAR_ALL_COL);
        // a filter half typed when the heading is clicked is sorted on, not dropped and not replayed
        const current = pendingFilters.current ?? filters;
        const newFilters = buildSortFilters(current, defaultColumns, sorted?.colId ?? null, sorted?.sort ?? null);

        commit(newFilters, sortingColumnChanged);
    }, [filters, defaultColumns, sortingColumnChanged, commit]);

    // the row the reader left off at, once the rows that offset counts are actually in the grid
    const restoreScroll = useCallback(() => {
        // a reload draws no rows, so the offset waits for them rather than scrolling to nothing
        if (scrollRestored.current || !scrollTop || showLoader || rows.length === 0 || !gridApi.current) {
            return;
        }
        scrollRestored.current = true;
        gridApi.current.ensureIndexVisible(Math.floor(scrollTop / ROW_HEIGHT), 'top');
    }, [scrollTop, showLoader, rows.length]);

    useEffect(restoreScroll, [restoreScroll]);

    const onGridReady = useCallback(({api}: GridReadyEvent<DataGridRow>) => {
        gridApi.current = api;
        restoreScroll();
    }, [restoreScroll]);

    // read as it scrolls rather than on the way out: by then ag-grid has torn its api down
    const onBodyScroll = useCallback(({top}: BodyScrollEvent<DataGridRow>) => {
        scrollOffset.current = top;
    }, []);

    useEffect(() => () => saveScrollPosition?.(scrollOffset.current), [saveScrollPosition]);

    const openRow = useCallback((row?: DataGridRow) => {
        if (row) {
            onRow?.(row).onClick?.();
        }
    }, [onRow]);

    // ag-grid listens on the row itself, below React's root container, so the select column
    // cannot keep a tick off the row by stopping propagation - it is recognised here instead
    const fromSelectColumn = (event: Event | null) => {
        const target = event?.target;
        return target instanceof Element && !!target.closest('.app-data-grid__select');
    };

    const onRowClicked = useCallback(({data, event}: RowClickedEvent<DataGridRow>) => {
        if (!fromSelectColumn(event)) {
            openRow(data);
        }
    }, [openRow]);

    const onCellKeyDown = useCallback(({data, event}: CellKeyDownEvent<DataGridRow>) => {
        if ((event as KeyboardEvent | null)?.key === 'Enter' && !fromSelectColumn(event)) {
            openRow(data);
        }
    }, [openRow]);

    // ag-grid makes the row a tab stop by itself, but cell focus is suppressed, so Enter on a
    // focused row never reaches onCellKeyDown - which is what would otherwise open it
    const onRowKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        if (event.key !== 'Enter' || !target.classList?.contains('ag-row')) {
            return;
        }
        openRow(gridApi.current?.getDisplayedRowAtIndex(Number(target.getAttribute('row-index')))?.data);
    }, [openRow]);

    // any filter set at all, the advanced ones outside the grid included - which is what decides
    // whether an empty list reads as "nothing matched" or as "nothing here yet"
    const filtersActive = Object.keys(filters ?? {}).some(key => {
        const value = filters[key];
        return !!value && typeof value === 'object' && Object.keys(value).length > 0;
    });

    const emptyState = useMemo(() => {
        if (noDataString) {
            return noDataString;
        }

        return filtersActive
            ? <div className="app-data-grid__empty">
                <div className="visma-pictogram visma-pictogram-search-code"/>
                <div className="title">{translate('grid.search.noResults')}</div>
                <div>{translate('grid.search.noMatchedResults')}</div>
                <div>{translate('grid.search.alterSearch')}</div>
            </div>
            : <div className="app-data-grid__empty">
                <div className="visma-pictogram visma-pictogram-empty-folder"/>
                <div className="title">{translate('grid.empty.noTasks')}</div>
                <div>{translate('grid.empty.readyForNewTask')}</div>
            </div>;
    }, [noDataString, filtersActive, translate]);

    // where the rows start, so what is drawn over them leaves the filter row clickable
    const rowsTop = HEADER_HEIGHT + (showFilters ? FILTER_ROW_HEIGHT : 0);

    return (
        <div className="app-data-grid__wrapper">
            <div className="app-data-grid__body">
                <DataGridFilterContext.Provider value={filterContext}>
                    <AppGrid<DataGridRow>
                        className={className ? `app-data-grid ${className}` : 'app-data-grid'}
                        dataCy={dataCy}
                        containerRef={setGridElement}
                        height={gridHeight}
                        rowData={showLoader ? [] : rows}
                        columnDefs={columnDefs}
                        getRowId={({data}: GetRowIdParams<DataGridRow>) => String(data.index)}
                        rowHeight={ROW_HEIGHT}
                        floatingFiltersHeight={FILTER_ROW_HEIGHT}
                        rowClass={onRow ? 'app-data-grid__row--clickable' : undefined}
                        onRowClicked={onRowClicked}
                        onCellKeyDown={onCellKeyDown}
                        {...(onRow && {onKeyDown: onRowKeyDown})}
                        onBodyScroll={onBodyScroll}
                        onBodyScrollEnd={onBodyScrollEnd}
                        onSortChanged={onSortChanged}
                        onGridReady={onGridReady}
                        // one field is all the backend sorts by, so a shift-click must not be able
                        // to leave two columns wearing an arrow
                        suppressMultiSort={true}
                        // the empty state is drawn below instead: ag-grid builds its overlay
                        // once and would keep the one it had while the first page was loading
                        suppressNoRowsOverlay={true}
                    />
                </DataGridFilterContext.Provider>

                {showLoader &&
                    <div className="app-data-grid__loading" style={{top: rowsTop}}>
                        <Loader size={loaderSize ?? LoaderSize.Medium}/>
                    </div>
                }

                {!showLoader && rows.length === 0 &&
                    <div className="app-data-grid__no-rows" style={{top: rowsTop}}>{emptyState}</div>
                }
            </div>

            <TableFooter records={records}
                         translate={translate}
                         loadingStatus={loadingStatus as never}
                         loadingError={loadingError as never}
                         asyncLoadingState={asyncLoadingState as never}
                         showRowData={!hideRowsNumber && records > 0}
                         onReload={loadMore}/>
        </div>
    );
};

export default AppDataGrid;
